import { featureFlagStore } from '@store/featureFlags/store';
import { fetchSavedSearches, saveFilter } from '@store/loadboard/services';
import { SavedSearchCategory } from '@store/loadboard/types';
import { ConfigManager } from '@utils/ConfigManager';
import { createError } from '@utils/errorUtils';
import { isRoutingFlagEnabled } from '@utils/routingFlagUtils';
import { createSignal } from 'solid-js';

import { V3RouteDef, v3Routes, v4RoutesV3Routes } from '../../routes';

export const getV3Route = (path: string): string => {
  const trimmedPath = path.replace('/', '');
  return `${ConfigManager.v3Host}/${trimmedPath}`;
};

export type RouteInformation = {
  isV3: boolean;
  route: string;
};

export const tryGetRoute = (
  route?: string | null | RouteInformation,
): RouteInformation => {
  if (route == undefined) {
    return {
      isV3: false,
      route: '',
    };
  }

  if (typeof route === 'object') {
    if (route.isV3) {
      return {
        isV3: true,
        route: `${ConfigManager.v3Host}${route.route}`,
      };
    }
    return {
      isV3: false,
      route: route.route,
    };
  }

  const knownV3routes = getV3RoutesExcludingV4Enabled();
  if (route.includes(ConfigManager.v3Host)) {
    route = route.replace(ConfigManager.v3Host, '');
  }
  const trimmedRoute = route.replace(/^\/+/, '').toLowerCase();
  for (const r of knownV3routes) {
    const parentPath = r.path.replace(/^\/+/, '').toLowerCase();
    const overridePath = r.overridePath?.replace(/^\/+/, '').toLowerCase();
    if (parentPath === trimmedRoute || overridePath === trimmedRoute) {
      return {
        isV3: true,
        route: `${ConfigManager.v3Host}/${overridePath ?? parentPath}`,
      };
    }
    if (r.subRoutes) {
      // eslint-disable-next-line max-depth
      for (const child of r.subRoutes) {
        const childPath = child.path.replace(/^\/+/, '');
        const combinedPath = `${parentPath}/${childPath}`;
        // eslint-disable-next-line max-depth
        // eslint-disable-next-line max-depth
        if (combinedPath.toLowerCase() === trimmedRoute) {
          return {
            isV3: true,
            route: `${ConfigManager.v3Host}/${parentPath}/${childPath}`,
          };
        }
      }
    }
  }
  return {
    isV3: false,
    route: route,
  };
};

const getV3RoutesExcludingV4Enabled = (): V3RouteDef[] => {
  const routes: V3RouteDef[] = [];
  v3Routes.forEach((route) => {
    if (route.routingFlag !== undefined) {
      // if there was an error in loading feature flags, we are going to check and see if we need to default to the v3 route
      //of course this means that well the user will be sent to v3 and will stay on v4 if the flag is actually off but there was an error
      // or there was a dragon attack on the servers
      if (featureFlagStore.isError) {
        if (route.defaultV3 !== undefined && route.defaultV3) {
          routes.push(route);
        }
      } else {
        // if the route has a feature flag, check if it's enabled
        const routingFlagIsEnabled = isRoutingFlagEnabled(route.routingFlag);
        if (!routingFlagIsEnabled) {
          routes.push(route);
        }
      }
    } else {
      // if the route doesn't have a feature flag, check to see if it should default to V3
      // if it should not need to send the user to v3, then let's not add it to the list and default the route to the V4 one in calling code
      // you don't need to know about what the caller is doing here, cause its just getting routes
      //but it is important to know why we are excluding routes that don't have a default to v3
      // some may say that the authour really likes to write comments, and they would be right
      if (route.defaultV3 !== undefined && route.defaultV3) {
        routes.push(route);
      }
    }
    if (route.subRoutes && route.subRoutes.length > 0) {
      route.subRoutes.forEach((subRoute) => {
        if (subRoute.routingFlag !== undefined) {
          if (featureFlagStore.isError) {
            if (subRoute.defaultV3 !== undefined && subRoute.defaultV3) {
              routes.push({
                ...subRoute,
                path: `${route.path}/${subRoute.path}`,
              });
            }
          } else {
            const subFlagEnabled = isRoutingFlagEnabled(subRoute.routingFlag);
            // if the subRoute has a feature flag, check if it's enabled
            if (!subFlagEnabled) {
              // Attach parent info if needed, or just push subRoute
              routes.push({
                ...subRoute,
                path: `${route.path}/${subRoute.path}`,
              });
            }
          }
        } else {
          if (subRoute.defaultV3 !== undefined && subRoute.defaultV3) {
            // if the subRoute doesn't have a feature flag, just push it
            routes.push({
              ...subRoute,
              path: `${route.path}/${subRoute.path}`,
            });
          }
        }
      });
    }
  });
  const ro = v4RoutesV3Routes;
  ro.forEach((route) => {
    if (route.routingFlag !== undefined) {
      // if the route has a feature flag, check if it's enabled
      if (featureFlagStore.isError) {
        if (route.defaultV3 !== undefined && route.defaultV3) {
          routes.push(route);
        }
      } else {
        const routingFlagIsEnabled = isRoutingFlagEnabled(route.routingFlag);
        if (!routingFlagIsEnabled) {
          routes.push(route);
        }
      }
    } else {
      // if the route doesn't have a feature flag, just push it
      if (route.defaultV3 !== undefined && route.defaultV3) {
        routes.push(route);
      }
    }
  });
  return routes;
};

const MUTE_SEARCH_NAME = 'muteNotifications';
const [flyoutsMuted, setFlyoutsMuted] = createSignal(false);
const [mutePreferenceReady, setMutePreferenceReady] = createSignal(false);

export { flyoutsMuted, mutePreferenceReady };

export const loadSidebarFlyoutMutePreference = async (): Promise<boolean> => {
  setMutePreferenceReady(false);
  try {
    const searches = await fetchSavedSearches();
    const muteSearch = searches.find(
      (search) =>
        search.category === SavedSearchCategory.SidePanel &&
        search.name === MUTE_SEARCH_NAME,
    );
    const parsed: unknown = JSON.parse(muteSearch?.params ?? '{}');
    const muted =
      !!parsed &&
      typeof parsed === 'object' &&
      (parsed as { muted?: unknown }).muted === true;
    setFlyoutsMuted(muted);
    return muted;
  } catch {
    setFlyoutsMuted(false);
    return false;
  } finally {
    setMutePreferenceReady(true);
  }
};

export const writeSidebarFlyoutMutePreference = async (
  muted: boolean,
): Promise<void> => {
  setFlyoutsMuted(muted);

  try {
    await saveFilter(
      { muted },
      SavedSearchCategory.SidePanel,
      MUTE_SEARCH_NAME,
    );
  } catch (error: unknown) {
    setFlyoutsMuted(!muted);
    throw createError(error, 'Failed to save mute preference');
  }
};

export const shouldShowSidebarFlyout = (
  isMuteToggleFeatureEnabled: boolean,
): boolean =>
  !isMuteToggleFeatureEnabled || (mutePreferenceReady() && !flyoutsMuted());
